import React from "react";
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { META_CONFIG } from '@/config/meta';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const MetaCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  // Wait for Supabase session to hydrate after Meta OAuth redirect
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) setSessionReady(true);
      }
    );
    // Check if session is already available
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!sessionReady) return;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        const errorMsg = errorDescription || errorParam;
        setError(errorMsg);
        toast.error(`Meta OAuth Error: ${errorMsg}`);
        setTimeout(() => navigate('/launcher/ad-sheets'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        toast.error('No authorization code received');
        setTimeout(() => navigate('/launcher/ad-sheets'), 3000);
        return;
      }

      try {
        // Exchange code for token via n8n-proxy edge function
        const { data: tokenData, error: proxyError } = await supabase
          .functions.invoke('n8n-proxy', {
            body: {
              action: 'META_OAUTH_EXCHANGE',
              payload: {
                code,
                redirect_uri: META_CONFIG.REDIRECT_URI,
              },
            },
          });

        if (proxyError) {
          throw new Error(`Token exchange failed: ${proxyError.message}`);
        }

        // Validate token response
        if (!tokenData || !tokenData.access_token) {
          throw new Error('Invalid token response from Meta. Please try again.');
        }

        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError || !user) {
          throw new Error('User not authenticated');
        }

        // Calculate expiration time with defensive parsing
        // Meta short-lived tokens default to 5184000 seconds (60 days)
        const expiresInSeconds = typeof tokenData.expires_in === 'number'
          ? tokenData.expires_in
          : parseInt(tokenData.expires_in, 10) || 5184000;
        const expiresAt = new Date(Date.now() + (expiresInSeconds * 1000));

        // Fetch Facebook user ID from Meta Graph API
        let metaUserId: string | null = null;
        try {
          const meResponse = await fetch(
            `https://graph.facebook.com/v24.0/me?fields=id&access_token=${tokenData.access_token}`
          );
          if (meResponse.ok) {
            const meData = await meResponse.json();
            metaUserId = meData.id || null;
          }
        } catch {
          // Non-blocking — continue without meta_user_id
        }

        // Upsert token to database
        const upsertPayload: Record<string, unknown> = {
          user_id: user.id,
          access_token: tokenData.access_token,
          token_type: tokenData.token_type || 'Bearer',
          expires_at: expiresAt.toISOString(),
          scope: tokenData.scope || META_CONFIG.SCOPE,
        };
        if (metaUserId) {
          upsertPayload.meta_user_id = metaUserId;
        }

        const { error: upsertError } = await supabase
          .from('meta_oauth_tokens')
          .upsert(upsertPayload as any, {
            onConflict: 'user_id',
          });

        if (upsertError) {
          throw upsertError;
        }

        // Invalidate all React Query caches and notify user
        queryClient.invalidateQueries();
        toast.info('Meta account connected — refreshing your data...');

        // Check user's onboarding status to decide where to redirect
        const { data: userData } = await supabase
          .from('users')
          .select('onboarding_status')
          .eq('id', user.id)
          .single();

        const currentStatus = (userData as any)?.onboarding_status;

        if (currentStatus === 'COMPLETED') {
          // Existing user reconnecting — sync ad accounts before navigating
          toast.info('Syncing your ad accounts...');

          try {
            await supabase.functions.invoke('n8n-proxy', {
              body: {
                action: 'SYNC_META',
                payload: {},
              },
            });
          } catch {
            // Non-blocking — continue even if sync fails
            console.error('Background sync after reconnect failed');
          }

          // Invalidate again so UI picks up fresh data
          queryClient.invalidateQueries();
          toast.success('Meta account connected successfully!');
          navigate('/settings?meta_connected=true');
        } else {
          // New user in onboarding — update status and continue setup
          await supabase
            .from('users')
            .update({ onboarding_status: 'META_CONNECTED' } as any)
            .eq('id', user.id);

          // Trigger background sync via proxy (fire-and-forget — proxy injects user_id)
          supabase.functions.invoke('n8n-proxy', {
            body: {
              action: 'SYNC_META',
              payload: {},
            },
          }).catch(() => {}); // fire-and-forget

          toast.success('Meta account connected! Let\'s set up your ad accounts.');
          navigate('/setup/select-accounts', { replace: true });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to connect Meta account';
        setError(errorMsg);
        toast.error(errorMsg);
        setTimeout(() => navigate('/launcher/ad-sheets'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, sessionReady]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        {error ? (
          <>
            <p className="text-destructive text-lg">{error}</p>
            <p className="text-muted-foreground">Redirecting to dashboard...</p>
          </>
        ) : (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="text-lg">Connecting your Meta account...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default MetaCallback;
